/**
 * A subject's marks between the API and the screen (UC-7, marks slices 01–02). The server
 * numbers the marks and derives their side; the client only reads them. A mark on a region,
 * view or kind the workspace does not know is left out rather than guessed. The card shows the
 * newest photo of each shot, and the close-up as the tattoo's crop once the worker cut it.
 */
import { type BodyRegion, getBodyRegion } from '@/components/humint/body-map/body-map-regions'
import {
  type BodyRegionId,
  INK_COLOURS,
  type InkColour,
  type Laterality,
  MARK_KINDS,
  type MarkKind,
  type MarkSummary,
  SKIP_REASONS,
  type SkipReason,
  TYPE10_CLASSES,
  type Type10Class,
} from '@/components/humint/types'
import type { MarkAttributesDto, MarkDto } from '@/services/api/humint-marks'
import type { SubjectMarkDto, SubjectMarkPhotoDto } from '@/services/api/humint-subject-page'
import { importedFields } from '../probe/photo-origin'
import { known } from './known'

const markKind = known<MarkKind>(MARK_KINDS)
const type10Class = known<Type10Class>(TYPE10_CLASSES)
const inkColour = known<InkColour>(INK_COLOURS)
const skipReason = known<SkipReason>(SKIP_REASONS)
const laterality = known<Laterality>(['left', 'right', 'center'])
const featureQuality = known<'ok' | 'low'>(['ok', 'low'])
const descriptionSource = known<'operator' | 'auto'>(['operator', 'auto'])
const autoDescriptionState = known<'available' | 'not_available'>(['available', 'not_available'])
/** Whether the deployment drafts descriptions; unknown (an older API) reads as available. */
const autoDescription = (value: string | undefined) => autoDescriptionState(value) ?? 'available'

type ShotRole = SubjectMarkPhotoDto['role']

/** The newest of one shot's photos (or skips): the server lists them oldest first. */
const newest = (photos: readonly SubjectMarkPhotoDto[], role: ShotRole) =>
  photos.filter(photo => photo.role === role).at(-1) ?? null

/** The mark's region on its own view; null for an id or a view the chart does not have. */
function regionOf(dto: SubjectMarkDto): BodyRegion | null {
  // The lookup validates the id: an unknown one finds nothing.
  const region = getBodyRegion(dto.region_id as BodyRegionId)
  return region && region.view === dto.view ? region : null
}

/** What the operator records about a mark, as the server holds it. */
function attributesOf(dto: MarkAttributesDto) {
  return {
    type10Class: type10Class(dto.type10_class),
    colours: dto.colours.map(inkColour).filter((colour): colour is InkColour => colour !== null),
    inscription: dto.inscription,
    description: dto.description,
    descriptionSource: descriptionSource(dto.description_source),
    autoDescription: autoDescription(dto.auto_description),
  }
}

/** One mark as the subject page and the body map show it, or null when the workspace cannot place it. */
export function toMarkSummary(dto: SubjectMarkDto, subjectRef: string): MarkSummary | null {
  const kind = markKind(dto.kind)
  const region = regionOf(dto)
  if (!kind || !region) return null
  const taken = dto.photos.filter(photo => photo.url !== null)
  const orientation = newest(taken, 'orientation')
  const skips = dto.photos.filter(photo => photo.url === null)
  const orientationSkip = orientation ? null : newest(skips, 'orientation')
  const latest = taken.at(-1)
  return {
    id: dto.mark_id,
    subjectId: subjectRef,
    number: dto.number,
    kind,
    view: region.view,
    regionId: region.id,
    pin: dto.pin,
    laterality: laterality(dto.laterality) ?? region.laterality,
    ...attributesOf(dto),
    closeupUrl: dto.crop_url ?? newest(taken, 'closeup')?.url ?? null,
    orientationUrl: orientation?.url ?? null,
    scaleUrl: newest(taken, 'scale')?.url ?? null,
    orientationSkipReason: skipReason(orientationSkip?.skip_reason),
    photoCount: taken.length,
    featureQuality: featureQuality(dto.feature_quality),
    pendingCandidates: dto.pending_candidates,
    capturedAt: latest?.captured_at ?? dto.created_at,
    ...importedFields(latest?.imported, latest?.taken_at),
  }
}

/** The subject's marks in pin-number order, each placed on the chart. */
export function toMarkSummaries(
  dtos: readonly SubjectMarkDto[],
  subjectRef: string
): MarkSummary[] {
  return dtos
    .map(dto => toMarkSummary(dto, subjectRef))
    .filter((mark): mark is MarkSummary => mark !== null)
    .sort((a, b) => a.number - b.number)
}

/** A mark after an edit: the attributes and description the server now holds, the rest as it was. */
export function withEditedMark(mark: MarkSummary, dto: MarkDto): MarkSummary {
  return { ...mark, kind: markKind(dto.kind) ?? mark.kind, ...attributesOf(dto) }
}
